type RecsysRequestSection = {
  sectionId: string;
  itemCount: number;
  sectionPosition: number;
};

type RegisterRecsysRequestInput = {
  streamId: string;
  streamKey: string;
  streamSessionId: string;
  requestUuid: string;
  page: number;
  sections: RecsysRequestSection[];
};

type RecsysRequestContext = {
  streamId: string;
  streamKey: string;
  streamSessionId: string;
  requestUuid: string;
  page: number;
  sections: RecsysRequestSection[];
};

const requestContextByUuid = new Map<string, RecsysRequestContext>();
const requestUuidBySectionId = new Map<string, string>();
const sectionPositionBySectionId = new Map<string, number>();
const latestRequestUuidByStreamId = new Map<string, string>();
const MAX_REQUEST_CONTEXTS = 1000;

let latestRequestUuid: string | undefined;

function getMostRecentRequestUuidForStream(
  streamId: string,
): string | undefined {
  const contexts = Array.from(requestContextByUuid.values());
  for (let i = contexts.length - 1; i >= 0; i -= 1) {
    const context = contexts[i];
    if (context.streamId === streamId) {
      return context.requestUuid;
    }
  }

  return undefined;
}

function evictRequestContext(requestUuid: string): void {
  const context = requestContextByUuid.get(requestUuid);
  if (!context) {
    return;
  }

  requestContextByUuid.delete(requestUuid);

  context.sections.forEach(({ sectionId }) => {
    if (requestUuidBySectionId.get(sectionId) === requestUuid) {
      requestUuidBySectionId.delete(sectionId);
    }
    sectionPositionBySectionId.delete(sectionId);
  });

  if (latestRequestUuid === requestUuid) {
    const remainingRequestUuids = Array.from(requestContextByUuid.keys());
    latestRequestUuid = remainingRequestUuids[remainingRequestUuids.length - 1];
  }

  if (latestRequestUuidByStreamId.get(context.streamId) === requestUuid) {
    const nextRequestUuid = getMostRecentRequestUuidForStream(context.streamId);
    if (nextRequestUuid) {
      latestRequestUuidByStreamId.set(context.streamId, nextRequestUuid);
    } else {
      latestRequestUuidByStreamId.delete(context.streamId);
    }
  }
}

function evictOldestRequestContextsIfNeeded(): void {
  while (requestContextByUuid.size > MAX_REQUEST_CONTEXTS) {
    const oldestRequestUuid = requestContextByUuid.keys().next().value;
    if (!oldestRequestUuid) {
      return;
    }

    evictRequestContext(oldestRequestUuid);
  }
}

export function registerRecsysRequestContext(
  input: RegisterRecsysRequestInput,
): void {
  requestContextByUuid.set(input.requestUuid, {
    streamId: input.streamId,
    streamKey: input.streamKey,
    streamSessionId: input.streamSessionId,
    requestUuid: input.requestUuid,
    page: input.page,
    sections: input.sections,
  });

  input.sections.forEach((section) => {
    requestUuidBySectionId.set(section.sectionId, input.requestUuid);
    sectionPositionBySectionId.set(section.sectionId, section.sectionPosition);
  });

  latestRequestUuidByStreamId.set(input.streamId, input.requestUuid);
  latestRequestUuid = input.requestUuid;
  evictOldestRequestContextsIfNeeded();
}

export function getRecsysRequestUuidForSectionId(
  sectionId: string | number,
): string | undefined {
  return requestUuidBySectionId.get(String(sectionId));
}

export function getRecsysSectionPositionForSectionId(
  sectionId: string | number,
): number | undefined {
  return sectionPositionBySectionId.get(String(sectionId));
}

export function getLatestRecsysRequestUuidForStream(
  streamId?: string,
): string | undefined {
  if (!streamId) {
    return latestRequestUuid;
  }

  return latestRequestUuidByStreamId.get(streamId) ?? latestRequestUuid;
}

export function getRecsysRequestContext(
  requestUuid: string,
): RecsysRequestContext | undefined {
  return requestContextByUuid.get(requestUuid);
}

export function getRecsysRequestContextsForStream(
  streamId: string,
): RecsysRequestContext[] {
  return Array.from(requestContextByUuid.values()).filter(
    (context) => context.streamId === streamId,
  );
}

export function getRecsysStreamSessionIdForRequest(
  requestUuid: string,
): string | undefined {
  return requestContextByUuid.get(requestUuid)?.streamSessionId;
}
